<template>
  <div v-if="formData.created_at" class="assignment-form" :class="{ history: isHistoryRecord }">
    <form>
      <section>
        <h1>{{ $t('assignment.labels.contractorDetails') }}</h1>
        <div class="form-group">
          <div class="form-control">
            <router-link
              class="contacts-list-link"
              :to="'/contractors/' + assignment.contractor.reference_number"
            >
              <ContactsList :contacts="[assignmentContractorProfile]" />
            </router-link>
          </div>
          <div class="form-control" :class="assignment.entity === 'SUPPLIER' && 'pay-to-link'">
            <label>{{ $t('assignment.labels.formLabels.payTo') }}</label>
            <template v-if="assignment.entity === 'SUPPLIER'">
              <router-link :to="'/suppliers/' + assignment.contractor.supplier_reference_number">
                <span>
                  {{ formData.contractor.supplier_name }}
                </span>
              </router-link>
            </template>
            <template v-else>
              <InputField
                v-model="formData.contractor.pay_to_name"
                type="text"
                class="form-input"
                readonly
              />
              <AddressDisplay :address="formData.contractor.address" />
            </template>
          </div>
        </div>
        <div class="form-group">
          <div class="form-control">
            <label>{{ $t('assignment.labels.formLabels.phone') }}</label>
            <InputField
              v-model="telephone"
              type="text"
              class="bordered"
              field-icon="phone-square"
              readonly
            />
          </div>

          <div class="form-control">
            <label>{{ $t('assignment.labels.formLabels.email') }}</label>
            <InputField
              v-model="formData.contractor.email"
              type="text"
              class="bordered"
              field-icon="envelope"
              readonly
            />
          </div>
          <div class="form-control">
            <label
              >{{ $t('assignment.labels.formLabels.payrollNumber')
              }}<span
                v-show="!formData.payroll_number && formData.status === 'SUBMITTED'"
                class="asterisk"
                >*</span
              ></label
            >
            <DropdownSelect
              v-if="!isNewPayorll"
              :menu-items="payrollNums"
              :selected-item="formData.payroll_number"
              class="bordered"
              :class="{ error: formErrors['Payroll Number'] }"
              @select-item="updatePayrollNum($event)"
            />
            <InputField
              v-if="isNewPayorll"
              type="text"
              class="bordered"
              field-icon="barcode"
              @input="onChange"
            />
          </div>
        </div>
        <div class="form-group">
          <div class="form-control">
            <label>{{ $t('assignment.labels.formLabels.hst') }}</label>
            <InputField
              v-model="privatizedHST"
              type="text"
              class="bordered"
              readonly
              @click="onToggleReveal"
            />
          </div>
          <div class="form-control">
            <label>{{ $t('assignment.labels.formLabels.backcheck') }}</label>
            <InputField
              v-model="formData.contractor.backcheck_date"
              type="text"
              class="bordered"
              field-icon="calendar-alt"
              readonly
            />
          </div>
          <div class="form-control">
            <label>{{ $t('assignment.labels.formLabels.entity') }}</label>
            <InputField
              v-model="formData.contractor.entity"
              type="text"
              class="bordered"
              readonly
            />
          </div>
        </div>
      </section>

      <section>
        <h1>{{ $t('assignment.labels.clientDetails') }}</h1>
        <div class="form-group">
          <div class="form-control">
            <router-link
              class="clients-display-link"
              :to="'/clients/' + assignment.client_reference"
            >
              <h2><ClientsDisplay :contacts="[assignmentClientProfile]" /></h2>
            </router-link>
          </div>
          <div class="form-control">
            <label
              >{{ $t('assignment.labels.formLabels.clientNumber')
              }}<span v-show="!formData.client_number" class="asterisk">*</span></label
            >
            <InputField
              v-model="formData.client_number"
              v-validate="'required'"
              type="text"
              class="bordered"
              :class="{ error: formErrors['client_number'] }"
              field-icon="barcode"
              name="Client #"
              @input="updateOnScreenValue(null, 'client_number', $event)"
            />
            <span class="error">{{ errors.first('Client #') }} </span>
          </div>

          <div class="form-control">
            <label>{{ $t('assignment.labels.formLabels.endClient') }}</label>
            <InputField
              v-if="formData.end_client_id"
              v-model="formData.end_client_name"
              type="text"
              class="bordered"
              name="End Client"
              picker="minus-circle"
              readonly
              @picker-click="removeOnScreenValue(formData.end_client_id, 'END_CLIENT')"
            />
            <tol-type-ahead
              v-else
              :search-params="{
                url: '/assignments/end-client-lookup',
                searchKeys: ['name']
              }"
              class="bordered"
              @select-item="onSelectSearchItem($event, 'END_CLIENT')"
            >
              <template v-slot="{ value }">
                {{ value.name }}
              </template>
            </tol-type-ahead>
          </div>
        </div>

        <div class="form-group">
          <div class="form-control">
            <label>{{ $t('assignment.labels.formLabels.workOrderNumber') }}</label>
            <InputField
              v-model="formData.work_order_number"
              v-validate="'required'"
              type="text"
              class="bordered"
              field-icon="barcode"
              name="Work Order #"
              @input="updateOnScreenValue(null, 'work_order_number', $event)"
            />
            <span class="error">{{ errors.first('Work Order #') }}</span>
          </div>
          <div class="form-control">
            <label>{{ $t('assignment.labels.formLabels.workAuthorization') }}</label>
            <InputField
              v-model="formData.work_authorization"
              type="text"
              class="bordered"
              name="WA"
              @input="updateOnScreenValue(null, 'work_authorization', $event)"
            />
          </div>
          <div class="form-control">
            <label>{{ $t('assignment.labels.formLabels.WOs_POs') }}</label>
            <div
              v-for="item in formData.purchaseOrderDetails"
              :key="item.purchase_order_number"
              class="tol-wos"
            >
              <font-awesome-icon
                icon="times"
                class="po-delete"
                @click="deletePO(item.purchase_order_number)"
              />

              <InputField
                v-model="item.work_order_number"
                v-validate="'required'"
                type="text"
                class="bordered"
                field-icon="barcode"
                name="WOsPOs #"
                @input="updateOnScreenValue(null, 'purchase_order_work_order', $event)"
              />
              <div class="po-display">
                <router-link :to="`/purchase-orders/${item.reference_number}`">{{
                  item.purchase_order_number
                }}</router-link>
              </div>
              <span class="error">{{ errors.first('WOsPOs #') }}</span>
            </div>

            <div v-if="showPurchaseOrderEffectiveDate" class="form-control">
              <label>{{ $t('assignment.labels.formLabels.purchaseOrderEffectiveDate') }}</label>
              <form-date-picker
                v-model="assignmentPurchaseOrderEffectiveDate"
                :class="{ error: formErrors['purchase_order_effective_date'] }"
                @change="onPurchaseOrderEffectiveDateChange"
              >
              </form-date-picker>
            </div>
            <tol-type-ahead
              :search-params="{
                url: '/assignments/purchase-order-quick-search',
                searchKeys: ['purchase_order_number']
              }"
              class="bordered"
              @select-item="updatePOWorkOrderList($event)"
            >
              <template v-slot="{ value }">
                {{ value.purchase_order_number }}
              </template>
            </tol-type-ahead>
          </div>
        </div>
        <div class="form-group">
          <div class="form-control">
            <label
              >{{ $t('assignment.labels.formLabels.billTo')
              }}<span v-show="!formData.billingMethod" class="asterisk">*</span></label
            >
            <tol-type-ahead
              v-if="!formData.billingMethod"
              :search-params="{
                url: '/assignments/billing-method-lookup',
                searchKeys: [
                  'attention',
                  'other',
                  'billing_methods.email',
                  'users_view.email',
                  'line1',
                  'line2',
                  'city',
                  'postal_code',
                  'province_abbr'
                ],
                client_id: formData.client_id,
                is_default: true
              }"
              class="blue"
              :allow-empty-search="true"
              @select-item="onSelectSearchItem($event, 'BILLING_METHOD')"
            >
              <template v-slot="{ value }">
                <billing-method-display :data="value" :show-remove="false" />
              </template>
            </tol-type-ahead>
            <billing-method-display
              v-else
              :data="formData.billingMethod"
              @remove="removeOnScreenValue(formData.billing_method_id, 'BILLING_METHOD')"
            />
          </div>
          <div class="form-control">
            <label
              >{{ $t('assignment.labels.formLabels.workLocation') }}
              <span v-show="!formData.work_location_province_abbr" class="asterisk">*</span>
            </label>

            <DropdownSelect
              :menu-items="provinceAbbr"
              :selected-item="formData.work_location_province_abbr"
              class="bordered"
              :class="{ error: formErrors['work_location_province_abbr'] }"
              @select-item="updateOnScreenValue(null, 'work_location_province_abbr', $event)"
            />
          </div>
        </div>

        <div class="form-control"></div>
      </section>

      <section>
        <h1>{{ $t('assignment.labels.stakeholdersDetails') }}</h1>
        <div class="form-control">
          <label>{{ $t('assignment.labels.formLabels.hiringManager') }}</label>
          <InputField
            v-model="formData.hiring_manager"
            type="text"
            class="bordered"
            field-icon="user"
            name="Hiring Manager"
            @input="updateOnScreenValue(null, 'hiring_manager', $event)"
          />
        </div>
        <div class="form-group">
          <div class="form-control wide" :class="{ error: accountManagerFieldHasError }">
            <div class="list-header">
              <font-awesome-icon icon="user-plus" @click="toggleTypeAhead('ACCOUNT_MANAGER')" />
            </div>
            <assignment-stakeholder-list
              :stakeholders="assignmentAccountManagers"
              title="Account Managers"
              @update="onSplitValueChange($event)"
              @remove="removeOnScreenValue($event.commission_profile_id, 'ACCOUNT_MANAGER')"
            />
            <tol-type-ahead
              v-show="typeAhead.ACCOUNT_MANAGER"
              :search-params="{
                url: '/assignments/user-quick-search?excludeNullCommissions=true',
                searchKeys: ['username', 'first_name', 'last_name', 'commission_number'],
                objectType: 'tesEmployee'
              }"
              class="blue"
              @select-item="setNewAssignmentStakeHolder($event, 'ACCOUNT_MANAGER')"
            >
              <template v-slot="{ value }">
                <span>{{ value.last_name }}, {{ value.first_name }}</span
                ><span v-if="value.commission_number"> # {{ value.commission_number }}</span>
              </template>
            </tol-type-ahead>
            <div
              v-if="clientValidation['ACCOUNT_MANAGER']"
              class="error-div"
              @click="clearClientValidation"
            >
              <font-awesome-icon icon="exclamation-circle" />{{
                clientValidation['ACCOUNT_MANAGER']
              }}
            </div>
          </div>
          <div class="form-control wide" :class="{ error: recruiterFieldHasError }">
            <div class="list-header">
              <font-awesome-icon icon="user-plus" @click="toggleTypeAhead('RECRUITER')" />
            </div>
            <assignment-stakeholder-list
              :stakeholders="assignmentRecruiters"
              title="Recruiters"
              @update="onSplitValueChange($event)"
              @remove="removeOnScreenValue($event.commission_profile_id, 'RECRUITER')"
            />
            <tol-type-ahead
              v-show="typeAhead.RECRUITER"
              :search-params="{
                url: '/assignments/user-quick-search?excludeNullCommissions=true',
                searchKeys: ['username', 'first_name', 'last_name', 'commission_number'],
                objectType: 'tesEmployee'
              }"
              class="blue"
              @select-item="setNewAssignmentStakeHolder($event, 'RECRUITER')"
            >
              <template v-slot="{ value }">
                <span>{{ value.last_name }}, {{ value.first_name }}</span
                ><span v-if="value.commission_number"> # {{ value.commission_number }}</span>
              </template>
            </tol-type-ahead>
            <div
              v-if="clientValidation['RECRUITER']"
              class="error-div"
              @click="clearClientValidation"
            >
              <font-awesome-icon icon="exclamation-circle" />{{ clientValidation['RECRUITER'] }}
            </div>
          </div>
        </div>
        <div class="form-group">
          <div class="form-control wide">
            <div class="list-header">
              <font-awesome-icon icon="user-plus" @click="toggleTypeAhead('OVERRIDE')" />
            </div>

            <assignment-stakeholder-list
              :stakeholders="assignmentCommissionOverrides"
              title="Commission Overrides"
              @remove="removeOnScreenValue($event.commission_profile_id, 'OVERRIDE')"
            />
            <tol-type-ahead
              v-show="typeAhead.OVERRIDE"
              :search-params="{
                url: '/assignments/user-quick-search?excludeNullCommissions=true',
                searchKeys: ['username', 'first_name', 'last_name', 'commission_number'],
                objectType: 'tesEmployee'
              }"
              class="blue"
              @select-item="setNewAssignmentStakeHolder($event, 'OVERRIDE')"
            >
              <template v-slot="{ value }">
                <span>{{ value.last_name }}, {{ value.first_name }}</span
                ><span v-if="value.commission_number"> # {{ value.commission_number }}</span>
              </template>
            </tol-type-ahead>
            <div
              v-if="clientValidation['OVERRIDE']"
              class="error-div"
              @click="clearClientValidation"
            >
              <font-awesome-icon icon="exclamation-circle" />{{ clientValidation['OVERRIDE'] }}
            </div>
          </div>
          <div
            class="form-control wide"
            :class="{ error: formErrors['stakeholderClientContacts'] }"
          >
            <div class="list-header">
              <font-awesome-icon icon="user-plus" @click="toggleTypeAhead('USER')" />
            </div>
            <tol-type-ahead
              v-show="typeAhead.USER"
              :search-params="{
                url: '/assignments/user-quick-search',
                searchKeys: ['username', 'first_name', 'last_name', 'email'],
                objectType: 'user',
                client_id: formData.client_id
              }"
              :allow-empty-search="true"
              class="blue"
              @select-item="onSelectSearchItem($event, 'USER')"
            >
              <template v-slot="{ value }">
                <div>
                  {{ value.last_name }}, {{ value.first_name }} (<font-awesome-icon
                    icon="laptop"
                  />{{ value.username }})
                </div>
                <div><font-awesome-icon icon="envelope" /> {{ value.email }}</div>
                {{ value.userType }}
              </template>
            </tol-type-ahead>
            <assignment-user-list
              :users="assignmentUsers"
              @update="updateOnScreenValue($event.userId, $event.permission)"
              @remove="removeOnScreenValue($event.userId, 'users')"
            />
            <div v-if="clientValidation['USER']" class="error-div" @click="clearClientValidation">
              <font-awesome-icon icon="exclamation-circle" />{{ clientValidation['USER'] }}
            </div>
          </div>
        </div>
      </section>

      <section>
        <h1>{{ $t('assignment.labels.miscDetails') }}</h1>
        <div class="form-group">
          <div class="form-control">
            <label
              >{{ $t('assignment.labels.formLabels.occupation')
              }}<span v-show="!formData.occupation" class="asterisk">*</span></label
            >
            <InputField
              v-model="formData.occupation"
              type="text"
              class="bordered"
              :class="{ error: formErrors['occupation'] }"
              @input="updateOnScreenValue(null, 'occupation', $event)"
            />
          </div>

          <div class="form-control">
            <label
              >{{ $t('assignment.labels.formLabels.startDate')
              }}<span v-show="!assignmentStartDate" class="asterisk">*</span></label
            >
            <form-date-picker
              v-model="assignmentStartDate"
              :type="formErrors['start_date'] ? 'bordered error' : 'bordered'"
              @change="onStartDateChange"
            >
            </form-date-picker>
          </div>
          <div class="form-control">
            <label>{{ $t('assignment.labels.formLabels.endDate') }}</label>
            <form-date-picker
              :value="assignmentStopDate"
              type="bordered"
              @change="onStopDateChange($event)"
            >
            </form-date-picker>
          </div>
        </div>
        <div class="form-group right">
          <div v-if="isAssignmentEndedEarly || formData.reason" class="form-control">
            <label>{{ $t('assignment.labels.formLabels.endReasons') }}</label>
            <DropdownSelect
              :menu-items="assignmentEndReasons"
              :selected-item="formData.reason"
              class="bordered"
              @select-item="updateAssignmentEndReason($event)"
            />
          </div>
        </div>

        <div class="form-group">
          <div class="form-control">
            <label>{{ $t('assignment.labels.formLabels.branch') }}</label>
            <DropdownSelect
              :menu-items="branchNames"
              :selected-item="formData.branch_name"
              class="bordered"
              disabled
            />
          </div>
          <div class="form-control">
            <label
              >{{ $t('assignment.labels.formLabels.division')
              }}<span v-show="!currentDivision" class="asterisk">*</span></label
            >
            <DropdownSelect
              :menu-items="divisionNames.map(d => d.abbr + ' ' + d.name)"
              :menu-objects="divisionNames"
              :selected-item="currentDivision"
              class="bordered"
              :class="{ error: formErrors['division'] }"
              @select-item="updateOnScreenValue(null, 'division', $event)"
            />
          </div>
          <div class="form-control">
            <label
              >{{ $t('assignment.labels.formLabels.department')
              }}<span v-show="!currentDepartment" class="asterisk">*</span></label
            >
            <DropdownSelect
              :menu-items="departmentDisplayNames"
              :menu-objects="departmentNames"
              :selected-item="currentDepartment"
              class="bordered"
              :class="{ error: formErrors['department'] }"
              @select-item="updateOnScreenValue(null, 'department', $event)"
            />
          </div>
        </div>

        <div class="form-group">
          <div class="form-control">
            <label
              >{{ $t('assignment.labels.formLabels.submittalPeriod')
              }}<span v-show="!selectedSubmittalPeriodDisplay" class="asterisk">*</span></label
            >
            <DropdownSelect
              :menu-items="submittalPeriodOptions"
              :selected-item="selectedSubmittalPeriodDisplay"
              class="bordered"
              :class="{ error: formErrors['submittal_period_type'] }"
              @select-item="handleSubmittalPeriodUpdate($event)"
            />
            <div v-if="showSubmittalPeriodEffectiveDate" class="rate-form">
              <label>{{ $t('assignment.labels.formLabels.submittalPeriodEffectiveDate') }}</label>
              <form-date-picker
                v-model="assignmentSubmittalPeriodEffectiveDate"
                :class="{ error: formErrors['effective_date'] }"
                @change="onSubmittalPeriodEffectiveDateChange($event)"
              >
              </form-date-picker>
            </div>
            <div
              v-if="clientValidation['submittal_period_type']"
              class="error-div"
              @click="clearClientValidation"
            >
              <font-awesome-icon icon="exclamation-circle" />{{
                clientValidation['submittal_period_type']
              }}
            </div>
          </div>
          <div class="form-control">
            <label
              >{{ $t('assignment.labels.formLabels.payPeriod')
              }}<span v-show="!formData.pay_period_type" class="asterisk">*</span></label
            >
            <DropdownSelect
              :menu-items="submittalPeriodOptions"
              :selected-item="formData.pay_period_type"
              class="bordered"
              :class="{ error: formErrors['pay_period_type'] }"
              @select-item="updateOnScreenValue(null, 'pay_period_type', $event)"
            />
          </div>
          <div class="form-control">
            <label>
              {{ $t('assignment.labels.formLabels.rebate') }}
              <span v-show="!currentRebate" class="asterisk">*</span>
            </label>
            <dropdown-select
              :menu-items="rebateOptions"
              :selected-item="currentRebate"
              :filter-on="['code', 'percent']"
              class="bordered"
              :class="{ error: formErrors['Rebate'] }"
              @select-item="updateOnScreenValue(null, 'rebate_id', $event)"
            >
              <template v-slot:selectedItem="{ value }">
                {{ value.code }} - {{ value.percent }}%
              </template>
              <template v-slot:dropdownItem="{ value }">
                {{ value.code }} - {{ value.percent }}%
              </template>
            </dropdown-select>
          </div>
        </div>
        <hr />
        <div class="form-group">
          <div class="form-control" :class="{ error: formErrors['expense_submittal'] }">
            <label
              >{{ $t('assignment.labels.formLabels.expensesSubmittal')
              }}<span v-show="!formData.expense_submittal" class="asterisk">*</span></label
            >

            <div class="single-line">
              <TolRadioButton
                button-label="Online"
                label="ONLINE"
                name="expenseSubmittal"
                :value="formData.expense_submittal"
                @toggle="updateOnScreenValue(null, 'expense_submittal', $event)"
              />
              <TolRadioButton
                button-label="Offline"
                label="OFFLINE"
                name="expenseSubmittal"
                :value="formData.expense_submittal"
                @toggle="updateOnScreenValue(null, 'expense_submittal', $event)"
              />
              <TolRadioButton
                button-label="None"
                label="NONE"
                name="expenseSubmittal"
                :value="formData.expense_submittal"
                @toggle="updateOnScreenValue(null, 'expense_submittal', $event)"
              />
            </div>
          </div>

          <div class="form-control" :class="{ error: formErrors['time_sheet_submittal'] }">
            <label class="field-label"
              >{{ $t('assignment.labels.formLabels.timeSheetsSubmittal')
              }}<span v-show="!formData.time_sheet_submittal" class="asterisk">*</span></label
            >
            <div class="single-line">
              <TolRadioButton
                button-label="Online"
                label="ONLINE"
                name="timeSheetsSubmittal"
                :value="formData.time_sheet_submittal"
                @toggle="updateOnScreenValue(null, 'time_sheet_submittal', $event)"
              />
              <TolRadioButton
                button-label="Offline"
                label="OFFLINE"
                name="timeSheetsSubmittal"
                :value="formData.time_sheet_submittal"
                @toggle="updateOnScreenValue(null, 'time_sheet_submittal', $event)"
              />
              <TolRadioButton
                button-label="None"
                label="NONE"
                name="timeSheetsSubmittal"
                :value="formData.time_sheet_submittal"
                @toggle="updateOnScreenValue(null, 'time_sheet_submittal', $event)"
              />
            </div>
          </div>
          <div class="form-control">
            <label class="field-label">{{
              $t('assignment.labels.formLabels.payStubsOnline')
            }}</label>
            <div class="single-line">
              <TolRadioButton
                button-label="Yes"
                label="Yes"
                name="depositSlipAccess"
                :value="isDepositSlipsOnline"
                @toggle="updateOnScreenValue(null, 'deposit_slip_access', $event)"
              />
              <TolRadioButton
                button-label="No"
                label="No"
                name="depositSlipAccess"
                :value="isDepositSlipsOnline"
                @toggle="updateOnScreenValue(null, 'deposit_slip_access', $event)"
              />
            </div>
          </div>
        </div>
        <div class="form-group">
          <div class="form-control">
            <label
              >{{ $t('assignment.labels.formLabels.payrollee')
              }}<span v-show="!isPayrollee" class="asterisk">*</span></label
            >
            <div class="single-line">
              <TolRadioButton
                button-label="Yes"
                label="Yes"
                name="payrollee"
                :value="isPayrollee"
                @toggle="updateOnScreenValue(null, 'is_referral', $event)"
              />
              <TolRadioButton
                button-label="No"
                label="No"
                name="payrollee"
                :value="isPayrollee"
                @toggle="updateOnScreenValue(null, 'is_referral', $event)"
              />
              <div class="payrollee-code">{{ formData.payrollee_code }}</div>
            </div>
          </div>
          <div class="form-control">
            <label
              >{{ $t('assignment.labels.formLabels.scheduleF')
              }}<span v-show="!formData.schedule_f" class="asterisk">*</span></label
            >
            <div class="single-line">
              <TolRadioButton
                button-label="Pending"
                label="Pending"
                name="scheduleF"
                :value="formData.schedule_f | camelCase"
                @toggle="formData.schedule_f = convertScheduleF($event)"
              />

              <TolRadioButton
                button-label="Received"
                label="Received"
                name="scheduleF"
                :value="formData.schedule_f | camelCase"
                @toggle="formData.schedule_f = convertScheduleF($event)"
              />
            </div>
            <div v-if="convertScheduleF(formData.schedule_f) === 'Received'" class="form-control">
              <label
                >{{ $t('assignment.labels.formLabels.assets')
                }}<span v-show="!hasAssets" class="asterisk">*</span></label
              >
              <div class="single-line">
                <TolRadioButton
                  button-label="No"
                  label="No"
                  name="assets"
                  :value="hasAssets"
                  @toggle="formData.assets = $event === 'Yes'"
                />

                <TolRadioButton
                  button-label="Yes"
                  label="Yes"
                  name="assets"
                  :value="hasAssets"
                  @toggle="formData.assets = $event === 'Yes'"
                />
              </div>
            </div>

            <div
              v-if="
                convertScheduleF(formData.schedule_f) === 'Received' && formData.assets === true
              "
              class="form-control"
            >
              <label>{{ $t('assignment.labels.formLabels.assetNumbers') }}</label>
              <InputField
                v-model="formData.asset_numbers"
                type="text"
                class="form-input"
                field-icon="barcode"
                name="Asset Numbers"
                @input="updateOnScreenValue(null, 'asset_numbers', $event)"
              />
            </div>
          </div>

          <div v-if="isExtraInfoEnabled" class="form-control">
            <label
              >{{ $t('assignment.labels.formLabels.ibmExtraInfo')
              }}<span v-show="!formData.ibm_extra_info" class="asterisk">*</span></label
            >
            <div class="single-line">
              <TolRadioButton
                button-label="Required"
                label="Required"
                name="IBMExtraInfo"
                :value="formData.ibm_extra_info"
                @toggle="formData.ibm_extra_info = $event"
              />

              <TolRadioButton
                button-label="N/A"
                label="N/A"
                name="IBMExtraInfo"
                :value="formData.ibm_extra_info"
                @toggle="formData.ibm_extra_info = $event"
              />
            </div>
          </div>
        </div>
        <hr />
        <div class="form-group">
          <div class="form-control wide" :class="{ error: formErrors['RATE'] }">
            <label>
              {{ $t('assignment.labels.formLabels.rates') }}
              <span v-show="!isInsertSelected && !formData.rates.length" class="asterisk">*</span>
            </label>
            <div class="list-header">
              <span @click="isInsertSelected = !isInsertSelected">+</span>
            </div>

            <assignment-rates-list
              :rates="assignmentRates"
              :rate-types="rateTypes"
              :insert-row="newAssignmentRate"
              :insert="isInsertSelected"
              :assignment-status="formData.status"
              @update="addRemoveRate($event)"
            />

            <div v-if="clientValidation['RATE']" class="error-div" @click="clearClientValidation">
              <font-awesome-icon icon="exclamation-circle" />{{ clientValidation['RATE'] }}
            </div>
          </div>

          <div class="form-control">
            <label
              >{{ $t('assignment.labels.formLabels.burden')
              }}<span v-show="!isClerical" class="asterisk">*</span></label
            >
            <div class="single-line">
              <TolRadioButton
                button-label="Clerical"
                label="Clerical"
                name="isClerical"
                :value="isClerical"
                @toggle="updateOnScreenValue(null, 'is_clerical', $event)"
              />
              <TolRadioButton
                button-label="Non Clerical"
                label="Non clerical"
                name="isClerical"
                :value="isClerical"
                @toggle="updateOnScreenValue(null, 'is_clerical', $event)"
              />
              <InputField
                v-model="formData.burden"
                type="text"
                class="bordered form-number-input wide"
                picker="percent"
                @input="updateOnScreenValue(null, 'burden', $event)"
              />
            </div>
          </div>
        </div>
      </section>

      <section v-if="formData.ibm_extra_info == 'Required'">
        <h1>{{ $t('assignment.labels.extraInfo') }}</h1>
        <IBMExtraInfoDetailsForm
          v-if="formData.ibm_extra_info === 'Required'"
          :extra-info="ibmExtraInfo"
          @IBMExtraInfoUpdate="onIBMExtraInfoUpdate"
        />
      </section>
      <section>
        <h1>{{ $t('assignment.labels.comments') }}</h1>
        <div class="form-group">
          <div class="form-control">
            <label>{{ $t('assignment.labels.formLabels.billingComments') }}</label>
            <textarea
              v-model="formData.bill_comments"
              rows="3"
              cols="30"
              @input="checkIfModified()"
            />
          </div>
          <div class="form-control">
            <label>{{ $t('assignment.labels.formLabels.paymentComments') }}</label>
            <textarea
              v-model="formData.pay_comments"
              rows="3"
              cols="30"
              @input="checkIfModified($event)"
            />
          </div>
          <div class="form-control">
            <label>{{ $t('assignment.labels.formLabels.generalComments') }}</label>
            <textarea
              v-model="formData.general_comments"
              rows="3"
              cols="30"
              @input="checkIfModified($event)"
            />
          </div>
        </div>
        <div class="form-control created-comment">
          <label>{{ $t('assignment.labels.formLabels.createdComments') }}</label>
          <textarea
            v-model="formData.created_comment"
            rows="3"
            cols="30"
            @input="checkIfModified($event)"
          />
        </div>
      </section>
      <div v-if="!isHistoryRecord" class="module-action-container">
        <div>
          <tol-button
            v-if="canThisUserSubmit"
            :button-type="'minor'"
            class="module-action submit"
            icon-name="upload"
            tooltip="SUBMIT"
            :disabled="
              approvedStatus || formData.status === 'SUBMITTED' || !formData.reference_number
            "
            @click="doAssignmentAction('SUBMIT')"
          >
          </tol-button>
          <tol-button
            v-if="canThisUserApprove"
            :button-type="'minor'"
            class="module-action approve"
            icon-name="check"
            tooltip="APPROVE"
            :disabled="approvedStatus || !formData.reference_number"
            @click="doAssignmentAction('APPROVE')"
          >
          </tol-button>

          <tol-button
            v-if="canThisUserReject"
            :button-type="'minor'"
            class="module-action reject"
            icon-name="times"
            tooltip="REJECT"
            :disabled="formData.status === 'REJECTED' || !formData.reference_number"
            @click="doAssignmentAction('REJECT')"
          >
          </tol-button>

          <tol-button
            v-if="canThisUserObsolete"
            :button-type="'minor'"
            class="module-action reject"
            icon-name="ban"
            tooltip="OBSOLETE"
            :disabled="formData.status === 'OBSOLETE' || !formData.reference_number"
            @click="doAssignmentAction('OBSOLETE')"
          >
          </tol-button>
        </div>
        <div>
          <tol-button
            v-if="canThisUserSave"
            id="save-btn"
            button-type="save"
            label="Save"
            :class="[
              'module-action',
              { disabled: !isOnScreenDataModified && formData.reference_number }
            ]"
            @click="doAssignmentAction('SAVE')"
          >
          </tol-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import Vue from 'vue';
import DropdownSelect from '@/app/components/DropdownSelect';
import InputField from '@/app/components/InputField';
import TolRadioButton from '@/app/components/TolRadioButton';
import FormDatePicker from '@/app/components/FormDatePicker';
import TolButton from '@/app/components/TolButton';
import BillingMethodDisplay from '@/app/components/BillingMethodDisplay';
import AddressDisplay from '@/app/components/AddressDisplay';
import { dateUtils } from '@/mixins/date-utils';
import ContactsList from '@/app/components/ContactsList';
import ClientsDisplay from '@/app/components/ClientsDisplay';
import TolTypeAhead from '@/app/components/TolTypeAhead';

import { mask } from '@/mixins/mask';

import format from 'date-fns/format';

import * as R from 'ramda';

import { AssignmentDetailsActions, AssignmentDetailsGetters } from '../assignment-details.keys';
import { AppGetters } from '@/app/app.keys';
import { TolErrorsGetters } from '@/app/features/tol-errors/tol-errors.keys';
import IBMExtraInfoDetailsForm from './IBMExtraInfoDetailsForm.vue';
import AssignmentUserList from './AssignmentUserList.vue';
import AssignmentRatesList from './AssignmentRatesList.vue';
import AssignmentStakeholderList from './AssignmentStakeholderList.vue';
import { userUtils } from '@/mixins/user-utils';

export default {
  name: 'AssignmentDetailsForm',
  components: {
    DropdownSelect,
    InputField,
    TolRadioButton,
    FormDatePicker,
    ClientsDisplay,
    TolButton,
    TolTypeAhead,
    ContactsList,
    AddressDisplay,
    BillingMethodDisplay,
    IBMExtraInfoDetailsForm,
    AssignmentUserList,
    AssignmentRatesList,
    AssignmentStakeholderList
  },
  mixins: [dateUtils, mask, userUtils],
  props: {
    assignment: {
      type: Object,
      required: true
    },
    isHistoryRecord: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showSpinner: false,
      isNewPayorll: false,
      submittalPeriodOptions: ['WEEKLY', 'BI_WEEKLY', 'SEMI_MONTHLY', 'MONTHLY'],
      revealHST: false,
      permissionColumns: ['user_name', 'email'],
      rateColumns: ['rate_code', 'amount_pay', 'amount_bill', 'hour_range'],
      formData: {},
      isOnScreenDataModified: false,
      isProcessFormBtnClicked: false,
      typeAhead: {
        ACCOUNT_MANAGER: false,
        RECRUITER: false,
        OVERRIDE: false,
        USER: false,
        BILLING_METHOD: false
      },
      isInsertSelected: false,
      isInsertStakeHolderSelected: false,
      isAddRateSelected: false,
      isAssignmentEndedEarly: false,
      isRemoveRateSelected: false,
      newAssignmentRate: {
        rate_code: '',
        amount_pay: 0.0,
        amount_bill: 0.0,
        hour_range: 0.0,
        effective_date: null,
        sort_order: 0
      },
      areFormFieldsDisabled: false,
      effectiveDateSelected: false,
      selectedRateCode: null,
      tooltipToShow: {
        code: null
      },
      clientValidation: {},
      showSubmittalPeriodEffectiveDate: false,
      sumittalPeriodEffectiveDateSelected: false,
      showPurchaseOrderEffectiveDate: false,
      purchaseOrderEffectiveDateSelected: false,
      selectedSubmittalPeriod: null,
      selectedPayrollNumber: null,
      draggingStartRate: null,
      dragginStopRate: null,
      dragEventY: null,
      ibmExtraInfo: {},
      region: ['West', 'Central', 'East'],
      newAssignmentStakeHolder: {}
    };
  },
  computed: {
    accountManagerFieldHasError() {
      return (
        this.formErrors['ACCOUNT_MANAGER'] ||
        (this.formErrors['stakeholderCommissions'] &&
          this.formErrors['stakeholderCommissions'].includes(
            this.$t('assignment.labels.validation.accountManagerRequired')
          ))
      );
    },
    recruiterFieldHasError() {
      return (
        this.formErrors['RECRUITER'] ||
        (this.formErrors['stakeholderCommissions'] &&
          this.formErrors['stakeholderCommissions'].includes(
            this.$t('assignment.labels.validation.recruiterRequired')
          ))
      );
    },
    assignmentContractorProfile() {
      let contractor_profile = {
        lastName:
          this.assignment.contractor.display_name || this.assignment.contractor.contractor_name,
        email: this.assignment.contractor.email,
        phone1: this.assignment.contractor.phone1,
        phone1_type: this.assignment.contractor.phone1_type
      };
      return contractor_profile;
    },
    assignmentClientProfile() {
      let client_profile = {
        lastName: this.assignment.client_name
      };
      return client_profile;
    },
    assignmentEndClientProfile() {
      let end_client_profile = {
        lastName: this.assignment.end_client_name
      };
      return end_client_profile;
    },
    newAccountManager() {
      return this.newAssignmentStakeHolder['ACCOUNT_MANAGER'];
    },
    isDetailsLoading() {
      return this.$store.getters[AssignmentDetailsGetters.IsAssignmentDetailsLoading];
    },
    btnDisplayString() {
      return this.isRemoveRateSelected ? 'Remove' : 'Add';
    },
    canThisUserSubmit() {
      if (this.currentUser) {
        const submitPerms = this.currentUser.permissions['ASSIGNMENT']['SUBMIT'];
        return submitPerms && submitPerms.includes(this.formData.status);
      }
      return false;
    },
    canThisUserApprove() {
      if (this.currentUser) {
        const approvePerms = this.currentUser.permissions['ASSIGNMENT']['APPROVE'];
        return approvePerms && approvePerms.includes(this.formData.status);
      }
      return false;
    },
    canThisUserObsolete() {
      if (this.currentUser) {
        const obsoletePerms = this.currentUser.permissions['ASSIGNMENT']['OBSOLETE'];
        return obsoletePerms && obsoletePerms.includes(this.formData.status);
      }
      return false;
    },
    canThisUserReject() {
      if (this.currentUser) {
        const rejectPerms = this.currentUser.permissions['ASSIGNMENT']['REJECT'];
        return rejectPerms && rejectPerms.includes(this.formData.status);
      }
      return false;
    },
    canThisUserSave() {
      if (this.currentUser) {
        const savePerms = this.currentUser.permissions['ASSIGNMENT']['SAVE'];
        return savePerms && savePerms.includes(this.formData.status);
      }
      return false;
    },

    currentUser() {
      return this.$store.state.currentUser;
    },
    approvedStatus() {
      return ['APPROVED', 'ACTIVE_ONLINE', 'ACTIVE_OFFLINE', 'FINISHED'].includes(
        this.formData.status
      );
    },
    formErrors() {
      return this.$store.getters.AssignmentValidationErrors;
    },
    rateTypes() {
      return this.$store.getters[AppGetters.AssignmentRateTypes];
    },
    rebateOptions() {
      return this.$store.getters[AppGetters.ClientRebates].filter(
        item => item.code === 'None' || (item.active && item.client_id === this.clientId)
      );
    },
    payrollNums() {
      let arr = this.formData.contractor.payrollnums
        ? this.formData.contractor.payrollnums.split(',')
        : this.formData.contractor.payrollNums.map(numbers => numbers.payroll_number);
      return arr;
    },
    currentRebate() {
      const rebate = this.$store.getters[AppGetters.ClientRebates].filter(
        item => item.id === this.formData.rebate_id
      )[0];
      return rebate;
    },
    currentDivision() {
      const division = this.$store.getters[AppGetters.DivisionAbbrs]
        .filter(item => item.abbr === this.formData.division_abbr)
        .map(d => d.abbr + ' ' + d.name)[0];

      return division;
    },
    assignmentId() {
      return this.$store.getters[AssignmentDetailsGetters.AssignmentId] || '';
    },
    clientId() {
      return this.formData.client_id || '';
    },
    currentSaveStatus() {
      return this.$store.getters[TolErrorsGetters.HasErrors] ? 'error' : 'success';
    },
    branchNames() {
      return this.$store.getters[AppGetters.BranchNames];
    },
    departmentNames() {
      return this.$store.getters[AppGetters.Departments];
    },
    departmentDisplayNames() {
      const departments = this.$store.getters[AppGetters.Departments];
      //checking IBM & Kyndryl refernce code to update department codes
      const val = departments.map(d => {
        const clientLabel =
          d.reference_number === 'J7NM7N'
            ? 'IBM'
            : d.reference_number === '4PQP48'
            ? 'Kyndryl'
            : 'Non-IBM' || '';
        const currencyLabel = d.currency_code === 'CAD' ? '' : `(${d.currency_code})`;

        return `${d.code} - ${d.branch_name} ${clientLabel} ${d.entity || ''} ${currencyLabel}`;
      });
      return val;
    },
    currentDepartment() {
      return this.departmentDisplayNames.filter(
        d => d.split(' - ')[0] === this.formData.department_code
      )[0];
    },
    divisionNames() {
      const divisions = this.$store.getters[AppGetters.DivisionAbbrs];
      return divisions;
    },
    provinceAbbr() {
      return this.$store.getters[AppGetters.ProvinceAbbreviations];
    },
    assignmentAccountManagers: {
      get() {
        return this.formData.stakeholderCommissions
          ? this.formData.stakeholderCommissions.filter(
              manager => manager.commission_type === 'ACCOUNT_MANAGER'
            )
          : [];
      }
    },
    assignmentRecruiters: {
      get() {
        return this.formData.stakeholderCommissions
          ? this.formData.stakeholderCommissions.filter(
              manager => manager.commission_type === 'RECRUITER'
            )
          : [];
      }
    },
    assignmentCommissionOverrides: {
      get() {
        return this.formData.stakeholderCommissions
          ? this.formData.stakeholderCommissions.filter(
              manager => manager.commission_type === 'OVERRIDE'
            )
          : [];
      }
    },
    assignmentRates: {
      get() {
        return this.formData.rates || [];
      }
    },
    assignmentEndReasons() {
      return this.$store.getters[AppGetters.AssignmentEndReasons].map(
        endReason => endReason.reason
      );
    },
    assignmentUsers() {
      return this.formData.stakeholderClientContacts || [];
    },
    privatizedHST: {
      get() {
        if (this.revealHST) return this.assignment.contractor.hst;
        else
          return this.assignment.contractor.hst
            ? this.maskHSTOrGSTOrQST(this.assignment.contractor.hst)
            : '';
      }
    },
    telephone: {
      get() {
        return this.assignment.contractor.phone1 + `(${this.assignment.contractor.phone1_type})`;
      }
    },
    payrollNumber: {
      get() {
        return this.formData.payroll_number || '';
      },
      set(val) {
        this.formData.payroll_number = val;
      }
    },
    backCheckDate: {
      get() {
        return this.formatDate(this.dummyAssignment.backcheck);
      }
    },
    assignmentStartDate() {
      return this.formData.start_date ? this.dateFromString(this.formData.start_date) : null;
    },
    assignmentStopDate() {
      return this.formData.stop_date ? this.dateFromString(this.formData.stop_date) : null;
    },
    assignmentSubmittalPeriodEffectiveDate() {
      return this.formData.submittal_period_effective_date
        ? this.dateFromString(this.formData.submittal_period_effective_date)
        : null;
    },
    assignmentPurchaseOrderEffectiveDate() {
      return this.formData.purchase_order_effective_date
        ? this.dateFromString(this.formData.purchase_order_effective_date)
        : null;
    },
    selectedSubmittalPeriodDisplay() {
      return this.formData.submittal_period_effective_date
        ? `${this.formData.submittal_period_type} (eff. ${
            this.formData.submittal_period_effective_date
          })`
        : this.formData.submittal_period_type;
    },
    isClerical: {
      get() {
        return this.formData.is_clerical ? 'Clerical' : 'Non clerical';
      }
    },
    isReferral: {
      get() {
        return this.formData.is_referral;
      }
    },
    isDepositSlipsOnline: {
      get() {
        return this.formData.deposit_slip_access === 'ONLINE' ? 'Yes' : 'No';
      }
    },
    isExpensesOnline: {
      get() {
        return this.formData.expense_submittal === 'ONLINE';
      }
    },
    hasAssets() {
      return this.formData.assets ? 'Yes' : 'No';
    },
    isPayrollee() {
      return this.formData.is_referral && this.formData.payrollee_code !== '_' ? 'Yes' : 'No';
    },
    isExtraInfoEnabled() {
      return this.isIBMOrKyndryl(this.formData.client_reference_number);
    }
  },
  watch: {
    assignment: {
      immediate: true,
      handler() {
        this.formData = R.clone(this.assignment);
        let contractorObj = this.formData.contractor;
        if (contractorObj.line1) {
          this.formData.contractor.address = {
            address_line1: contractorObj.line1,
            address_line2: contractorObj.line2,
            city: contractorObj.city,
            postal_code: contractorObj.postal_code,
            province_abbr: contractorObj.province_abbr
          };
        }
        if (this.formData.ibmExtraInfo) {
          this.ibmExtraInfo = this.formData.ibmExtraInfo;
        }
        if (
          this.formData.contractor.entity === 'SUPPLIER' &&
          !this.formData.contractor.pay_to_name
        ) {
          this.formData.contractor.pay_to_name = this.formData.supplier_name;
        }
      }
    },
    dragEventY: {
      immediate: true,
      handler() {
        if (this.formData.rates) {
          this.formData.rates = this.formData.rates.sort((a, b) => {
            return a.sort_order - b.sort_order;
          });
        }
      }
    }
  },
  updated() {
    if (this.isHistoryRecord && !this.areFormFieldsDisabled) {
      const forms = document.querySelectorAll('.assignment-form.history form');

      forms.forEach(form => this.disableFormElements(form));
      this.areFormFieldsDisabled = true;
    }
  },
  destroyed() {
    this.$store.dispatch(AssignmentDetailsActions.SetAssignmentValidationErrors);
  },
  methods: {
    checkIfModified() {
      this.isOnScreenDataModified = true;
    },
    convertScheduleF(value) {
      return Vue.filter('camelCase')(value);
    },
    onChange(event) {
      this.formData['payroll_number'] = event;
    },
    onDragStart(evt, val) {
      this.draggingStartRate = val;
    },

    onDragEnter(evt, val) {
      this.dragEventY = evt.clientY;
      if (this.draggingStartRate) {
        const swap = {
          [this.draggingStartRate.rate_code]: val.sort_order,
          [val.rate_code]: this.draggingStartRate.sort_order
        };
        this.formData.rates = this.formData.rates.map(rate => {
          if (swap[rate.rate_code]) {
            rate.sort_order = swap[rate.rate_code];
          }
          return rate;
        });
      }
    },
    onDragStop(val) {
      this.isOnScreenDataModified = true;
      this.draggingStopRate = val;
    },
    updatePayrollNum(event) {
      if (this.isHistoryRecord) {
        return;
      }
      const payroll = this.formData.contractor.payrollNums.filter(payrollNumbers => {
        return payrollNumbers.payroll_number === event;
      });
      this.formData.payroll_number = event;
      this.formData.payrollnum_id = payroll[0].id;
      this.isOnScreenDataModified = true;
    },

    addRemoveRate(evt) {
      if (evt.action === 'add') {
        this.addOnScreenValue(evt.data, 'RATE');
      } else if (evt.action === 'remove') {
        this.removeOnScreenValue(evt.data, 'rates');
      }
    },
    clearClientValidation() {
      this.clientValidation = {};
    },
    setClientValidation(formField, message) {
      this.$set(this.clientValidation, formField, message);
      window.setTimeout(() => {
        this.clientValidation = {};
      }, 4000);
    },
    getAlertReferenceNumbers(message) {
      let result;
      if (Array.isArray(message.referenceNumber)) {
        result = message.referenceNumber.map(ref => {
          return { referenceNumber: ref };
        });
      } else {
        result = [{ referenceNumber: message.referenceNumber }];
      }
      return result;
    },
    disableFormElements(form) {
      for (let i = 0; i < form.elements.length; i++) {
        form.elements[i].disabled = true;
      }
      const tolTables = form.querySelectorAll('.tol-table');
      tolTables.forEach(tolTable => tolTable.classList.add('history'));

      const removeButtons = form.querySelectorAll('div.remove');
      removeButtons.forEach(removeButton => removeButton.classList.add('history'));

      const listHeaderButtons = form.querySelectorAll('.list-header span');
      listHeaderButtons.forEach(listHeaderButton => listHeaderButton.classList.add('history'));

      const checkboxes = form.querySelectorAll('.checkbox');
      checkboxes.forEach(box => box.classList.add('is-readonly'));

      const dropDowns = form.querySelectorAll('.dropdown-container');
      dropDowns.forEach(dropdown => dropdown.setAttribute('disabled', 'true'));
    },
    changeNewRateCode(event) {
      this.newAssignmentRate.rate_code = event.code;
    },
    onEffectiveDateChange(e) {
      if (e) {
        this.newAssignmentRate.effective_date = e;
        this.effectiveDateSelected = true;
      }
    },
    updateAssignmentEndReason(event) {
      const reason = this.$store.getters[AppGetters.AssignmentEndReasons].filter(
        assignmentEndReasons => {
          return assignmentEndReasons.reason === event;
        }
      );
      this.formData.reason_id = reason[0].id;
    },
    setNewAssignmentStakeHolder(event, key) {
      this.typeAhead[key] = false;
      const commissionUserObject = {
        assignment_id: this.assignmentId,
        commission_number: event.commission_number,
        commission_profile_id: event.commission_profile_id,
        commission_type: key,
        sort_order: 0,
        split: 0.0,
        tes_employee_id: event.tes_employee_id,
        display_name: event.display_name,
        email: event.email
      };
      this.addOnScreenValue(commissionUserObject, key);
    },
    onSelectSearchItem(event, key) {
      this.typeAhead[key] = false;
      const commissionUserObject = {
        assignment_id: this.assignmentId,
        commission_number: event.commission_number,
        commission_profile_id: event.commission_profile_id,
        commission_type: key,
        sort_order: 0,
        split: 0.0,
        tes_employee_id: event.tes_employee_id,
        display_name: event.display_name,
        email: event.email
      };

      const userObject = {
        assignment_id: this.assignmentId,
        can_approve_expenses: false,
        can_approve_time_sheets: false,
        can_submit_expenses: false,
        can_submit_time_sheets: false,
        can_view_time_sheets: false,
        can_view_expenses: false,
        can_view_deposit_slips: false,
        display_name: event.display_name,
        user_id: event.id,
        email: event.email
      };
      if (key === 'BILLING_METHOD') {
        this.formData.billingMethod = event;
        this.formData.billing_method_id = event.id;
      }
      if (key === 'END_CLIENT') {
        this.formData.end_client_id = event.id;
        this.formData.end_client_name = event.name;
      }
      if (key === 'USER') this.addOnScreenValue(userObject, key);
      else this.addOnScreenValue(commissionUserObject, key);
    },
    toggleTypeAhead(key) {
      this.typeAhead[key] = !this.typeAhead[key];
    },
    onToggleReveal() {
      this.revealHST = !this.revealHST;
    },
    formatDate(d) {
      return d ? format(d, 'YYYY-MM-DD') : null;
    },
    onStartDateChange(event) {
      if (this.isHistoryRecord) {
        return;
      }
      this.formData.start_date = this.formatDate(event);
      this.isOnScreenDataModified = true;
    },
    onStopDateChange(event) {
      if (this.formatDate(event) < this.formatDate(this.assignmentStopDate)) {
        this.formData.oldEndDate = this.formatDate(this.assignmentStopDate);
        this.isAssignmentEndedEarly = true;
      }
      if (this.isHistoryRecord) {
        return;
      }
      this.formData.stop_date = this.formatDate(event);

      this.isOnScreenDataModified = true;
    },
    updateOnScreenValue(id = null, fieldName, event = null) {
      if (this.isHistoryRecord) return;
      this.updateUserPermissions(id, fieldName);
      this.updateClientFields(fieldName, event);
      this.updateMiscFields(fieldName, event);
      this.updateAdditionalMiscFields(fieldName, event);
      this.isOnScreenDataModified = true;
    },
    deletePO(poNumber) {
      if (this.isHistoryRecord) return;
      this.formData.purchaseOrderDetails = this.formData.purchaseOrderDetails.filter(item => {
        if (item.purchase_order_number != poNumber) {
          return item;
        }
      });
    },
    updatePOWorkOrderList(event = null) {
      if (this.isHistoryRecord) return;
      if (!this.formData.purchaseOrderDetails) {
        this.$set(this.formData, 'purchaseOrderDetails', []);
      }
      this.formData.purchaseOrderDetails.push({
        purchase_order_number: event.purchase_order_number,
        work_order_number: null,
        assignment_id: event.assignment_id,
        purchase_order_id: event.id,
        reference_number: event.reference_number
      });
      this.showPurchaseOrderEffectiveDate = true;
    },
    addOnScreenValue(userObject, userType) {
      switch (userType) {
        case 'ACCOUNT_MANAGER':
        case 'RECRUITER':
        case 'OVERRIDE':
          this.$store.dispatch(
            AssignmentDetailsActions.DeleteAssignmentValidationErrorKey,
            userType
          );
          if (!this.formData.stakeholderCommissions) {
            this.formData['stakeholderCommissions'] = new Array();
          }
          if (
            this.formData.stakeholderCommissions.findIndex(
              s =>
                s.commission_number === userObject.commission_number &&
                s.commission_type === userObject.commission_type
            ) === -1
          ) {
            this.formData.stakeholderCommissions.push(userObject);
          } else {
            const msg = `${userObject.display_name} #${
              userObject.commission_number
            } already added.`;
            this.setClientValidation(userType, msg);
          }
          break;
        case 'USER':
          this.$store.dispatch(
            AssignmentDetailsActions.DeleteAssignmentValidationErrorKey,
            'stakeholderClientContacts'
          );
          if (!this.formData.stakeholderClientContacts) {
            this.formData['stakeholderClientContacts'] = new Array();
          }
          if (
            this.formData.stakeholderClientContacts.findIndex(
              u => u.user_id === userObject.user_id
            ) === -1
          ) {
            this.formData.stakeholderClientContacts.push(userObject);
          } else {
            const msg = `${userObject.display_name} ${userObject.email} already added.`;
            this.setClientValidation(userType, msg);
          }
          break;
        case 'RATE':
          this.handleAddAssignmentRate(userObject, userType);
          break;
      }

      this.isOnScreenDataModified = true;
    },
    removeOnScreenValue(prop, fieldName) {
      let updatedManagers = [];
      let updatedUsers = [];
      let updatedRates = [];
      let [isEffectiveDateValid, msg] = [null, null];
      switch (fieldName) {
        case 'ACCOUNT_MANAGER':
        case 'RECRUITER':
        case 'OVERRIDE':
          updatedManagers = this.formData.stakeholderCommissions.filter(manager => {
            return !(
              manager.commission_profile_id === prop && manager.commission_type === fieldName
            );
          });
          this.formData.stakeholderCommissions = updatedManagers;
          break;
        case 'users':
          updatedUsers = this.formData.stakeholderClientContacts.filter(
            user => user.user_id !== prop
          );
          this.formData.stakeholderClientContacts = updatedUsers;
          break;
        case 'rates':
          if (!prop.effective_date) {
            updatedRates = this.formData.rates.filter(rate => rate.rate_code !== prop.rate_code);
            this.formData.rates = updatedRates;
            this.isRemoveRateSelected = false;
            this.selectedRateCode = null;
          } else {
            [isEffectiveDateValid, msg] = this.checkEffectiveDate(prop.effective_date);
            if (isEffectiveDateValid) {
              updatedRates = this.formData.rates.filter(rate => rate.rate_code !== prop.rate_code);
              const newEffectiveDate = format(prop.effective_date, 'YYYY-MM-DD');
              /**
               * TODO
               * This implementation needs to be evaluated, and possibly changed
               * Do we want to update all the rates' effective date, when only one was changed?
               * 2023/07/26
               * Jira Ticket: TOL4-872
               */
              updatedRates.forEach(rate => (rate.effective_date = newEffectiveDate));
              this.formData.rates = updatedRates;
              this.isRemoveRateSelected = false;
              this.selectedRateCode = null;
            } else {
              this.setClientValidation('RATE', msg);
            }
          }
          break;
        case 'BILLING_METHOD':
          this.formData.billing_method_id = null;
          this.formData.billingMethod = null;
          break;
        case 'END_CLIENT':
          this.formData.end_client_id = null;
          this.formData.end_client_name = null;
          break;
      }
      this.isOnScreenDataModified = true;
    },
    onSplitValueChange(event) {
      this.$store.dispatch(
        AssignmentDetailsActions.DeleteAssignmentValidationErrorKey,
        event.commission_type
      );

      const updateIndex = this.formData.stakeholderCommissions.findIndex(manager => {
        return (
          manager.commission_profile_id === event.commission_profile_id &&
          manager.commission_type === event.commission_type
        );
      });

      this.formData.stakeholderCommissions[updateIndex].split = event.split;

      this.isOnScreenDataModified = true;
    },
    updateUserPermissions(id, fieldName) {
      let updateIndex = -1;
      switch (fieldName) {
        case 'can_approve_time_sheets':
        case 'can_submit_time_sheets':
        case 'can_view_time_sheets':
        case 'can_approve_expenses':
        case 'can_submit_expenses':
        case 'can_view_expenses':
          updateIndex = this.formData.stakeholderClientContacts.findIndex(
            approver => approver.user_id === id
          );
          this.formData.stakeholderClientContacts[updateIndex][fieldName] = !this.formData
            .stakeholderClientContacts[updateIndex][fieldName];
          this.$store.dispatch(
            AssignmentDetailsActions.DeleteAssignmentValidationErrorKey,
            'stakeholderClientContacts'
          );
          break;
      }
    },
    updateClientFields(fieldName, event) {
      switch (fieldName) {
        case 'work_location_province_abbr':
        case 'client_number':
        case 'work_order_number':
        case 'payroll_number':
        case 'hiring_manager':
          this.formData[fieldName] = event;
          break;
      }
    },
    updateMiscFields(fieldName, event) {
      switch (fieldName) {
        case 'pay_period_type':
        case 'branch_name':
        case 'time_sheet_submittal':
        case 'expense_submittal':
        case 'occupation':
        case 'burden':
          this.formData[fieldName] = event;
          break;
        case 'is_clerical':
          this.formData.is_clerical = event === 'Clerical';
          break;
        case 'deposit_slip_access':
          this.formData[fieldName] = event === 'Yes' ? 'ONLINE' : 'OFFLINE';
          break;
        case 'is_referral':
          this.formData[fieldName] = event.checked;
          break;
        case 'rebate_id':
          this.formData[fieldName] = event.id;
          break;
        case 'division':
          this.formData.division_abbr = event.abbr;
          break;
        case 'department':
          typeof event === 'object'
            ? (this.formData.department_code = event.code)
            : (this.formData.department_code = event.split('-')[0].trim());
          break;
      }
    },
    //Function is created to resolve the complexity of updateMiscFields function
    updateAdditionalMiscFields(fieldName, event) {
      switch (fieldName) {
        case 'is_referral':
          this.formData.is_referral = event === 'Yes';
          break;
      }
    },
    doAssignmentAction(action) {
      if (!this.currentUser.permissions['ASSIGNMENT'][action]) return;
      if (!this.isOnScreenDataModified && this.formData.reference_number && action === 'SAVE') {
        return;
      }
      const pastTenseAction = this.$options.filters.pasttense(action).toUpperCase();
      if (pastTenseAction === this.formData.status) {
        return;
      }
      if (
        (pastTenseAction === 'SUBMITTED' || pastTenseAction === 'APPROVED') &&
        ['ACTIVE_ONLINE', 'ACTIVE_OFFLINE', 'FINISHED'].includes(this.formData.status)
      ) {
        return;
      }
      if (!this.formData.reference_number && action !== 'SAVE') {
        return;
      }
      if (this.formData.ibm_extra_info == 'Required') {
        this.formData['ibmExtraInfo'] = { ...this.ibmExtraInfo };
      }
      this.isNewPayorll = false;
      this.isProcessFormBtnClicked = true;
      this.showSpinner = true;
      this.showPurchaseOrderEffectiveDate = false;
      if (this.formData.schedule_f) {
        this.formData.schedule_f = this.formData.schedule_f.toUpperCase();
      }
      this.$store.dispatch(AssignmentDetailsActions.DoAssignmentAction, {
        assignment: this.formData,
        action
      });
    },
    checkEffectiveDate(effectiveDate) {
      if (!this.assignmentStartDate) {
        return [false, this.$t('assignment.labels.validation.setStartDateBeforeEffective')];
      }
      if (this.assignmentStartDate && !this.assignmentStopDate) {
        if (effectiveDate >= this.assignmentStartDate) {
          return [true, null];
        } else {
          return [false, this.$t('assignment.labels.validation.effectiveEarlierThanStartDate')];
        }
      } else {
        if (effectiveDate <= this.assignmentStopDate && effectiveDate >= this.assignmentStartDate) {
          return [true, null];
        } else {
          return [false, this.$t('assignment.labels.validation.effectiveOutsideAssignmentDates')];
        }
      }
    },
    handleAddAssignmentRate(userObject, userType) {
      let newRate = {};
      this.$store.dispatch(AssignmentDetailsActions.DeleteAssignmentValidationErrorKey, userType);
      this.isAddRateSelected = false;
      if (userObject.rate_code === '') {
        userObject.rate_code = this.rateTypes[0].code;
      }
      newRate = Object.assign({}, userObject);
      const [isEffectiveDateValid, msg] = this.checkEffectiveDate(newRate.effective_date);
      if (!this.formData.rates) {
        this.formData['rates'] = new Array();
      }
      if (
        this.formData.rates.findIndex(r => r.rate_code === newRate.rate_code) === -1 &&
        newRate.effective_date &&
        isEffectiveDateValid
      ) {
        const newEffectiveDate = format(newRate.effective_date, 'YYYY-MM-DD');
        this.formData.rates.forEach(rate => (rate.effective_date = newEffectiveDate));
        newRate.effective_date = newEffectiveDate;
        if (newRate.rate_code === 'RG') {
          newRate.sort_order = 0;
        } else {
          newRate.sort_order =
            this.formData.rates.length != 0
              ? this.formData.rates.length
              : this.formData.rates.length + 1;
        }
        this.formData.rates.push(newRate);
        this.formData.rates = this.formData.rates.sort((a, b) => {
          return a.sort_order - b.sort_order;
        });
      } else {
        this.setClientValidation('RATE', msg || `Rate code ${newRate.rate_code} already added`);
      }

      this.effectiveDateSelected = false;
      this.newAssignmentRate.rate_code = '';
      this.newAssignmentRate.amount_pay = 0.0;
      this.newAssignmentRate.amount_bill = 0.0;
      this.newAssignmentRate.hour_range = 0.0;
      this.newAssignmentRate.effective_date = null;
      this.isInsertSelected = false;
    },
    handleSubmittalPeriodUpdate(period) {
      this.selectedSubmittalPeriod = period;
      if (this.formData.submittal_period_type) {
        this.showSubmittalPeriodEffectiveDate = true;
      } else {
        this.formData.submittal_period_type = period;
      }
    },
    onSubmittalPeriodEffectiveDateChange(evt) {
      if (evt) {
        const [isEffectiveDateValid, msg] = this.checkEffectiveDate(evt);

        if (isEffectiveDateValid) {
          this.formData.submittal_period_effective_date = this.formatDate(evt);
          this.formData.submittal_period_type = this.selectedSubmittalPeriod;
          this.setSubmittalPeriodEffectiveDate();
        } else {
          this.setClientValidation('submittal_period_type', msg);
          this.formData.submittal_period_effective_date = null;
          this.showSubmittalPeriodEffectiveDate = false;
        }
      } else {
        this.formData.submittal_period_type = this.selectedSubmittalPeriod;
        this.setSubmittalPeriodEffectiveDate();
      }
    },
    onPurchaseOrderEffectiveDateChange(evt) {
      if (evt) {
        const [isEffectiveDateValid, msg] = this.checkEffectiveDate(evt);
        if (isEffectiveDateValid) {
          this.formData.purchase_order_effective_date = this.formatDate(evt);
        } else {
          this.setClientValidation('purchase_order', msg);
          this.showPurchaseOrderEffectiveDate = false;
        }
      }
      this.isOnScreenDataModified = true;
    },
    setSubmittalPeriodEffectiveDate() {
      this.isOnScreenDataModified = true;
      this.sumittalPeriodEffectiveDateSelected = false;
      this.showSubmittalPeriodEffectiveDate = false;
    },
    onIBMExtraInfoUpdate(value) {
      this.isOnScreenDataModified = true;
      this.ibmExtraInfo = { ...value };
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';
@import '@/app/styles/action-buttons.scss';

.created-comment {
  width: 100% !important;
}

.asterisk {
  color: red;
}

.assignment-form {
  &.history {
    font-size: 0.8em;
    background-color: $light-grey-2;
    padding: 25px;
  }
  .update-message {
    margin-top: -$alert-vertical-margin;
    position: fixed;
    top: 0;
    right: 0;
    width: 40%;
    z-index: 999;

    .slide-leave-active,
    .slide-enter-active {
      transition: transform 1s;
    }
    .slide-enter {
      transform: translate(100%, 0);
    }
    .slide-leave-to {
      transform: translate(-100%, 0);
    }
  }

  .contacts-list-link {
    text-decoration: none;
    color: black;
  }

  .clients-display-link {
    text-decoration: none;
    color: black;
    box-shadow: inset 0 -3px 0 0 #bdb;
  }

  form {
    width: 100%;

    section {
      section {
        padding: 0;
      }
      padding: 0 40px;
      margin-bottom: 20px;
      background-color: $background-level-3;
      h1 {
        padding-top: 15px;
        border-bottom: 2px solid $blue;
      }

      h1,
      h2 {
        flex: 0 100%;
      }
      .form-group {
        display: flex;
        justify-content: space-between;
        &.right {
          justify-content: flex-end;
        }

        .pay-to-link {
          padding: 25px 15px;

          &:hover {
            cursor: pointer;
            background-color: $mid-blue;
            border-radius: 8px;
          }

          a {
            text-decoration: none;
            color: black;
          }
        }
      }

      .form-control {
        padding: 10px 0;
        width: 350px;

        &.wide {
          width: 500px;
          &.error {
            background-color: $modified-form;
          }
        }
        .tol-wos {
          display: flex;
        }
        > label {
          display: block;
          font-weight: bold;
        }

        input,
        textarea,
        select {
          display: block;
          width: 100%;
          padding: 5px 0;
        }

        .binary-controls {
          padding: 10px 0;
          > label {
            font-weight: bold;
          }
        }
        .error,
        .error-div {
          color: $red;
          background-color: $modified-form;
        }
        .error-div {
          padding: 8px;
          &:hover {
            cursor: pointer;
          }
          svg {
            margin-right: 15px;
          }
        }

        .end-label {
          display: inline-block;
          padding: 8px 5px;
          background-color: $light-grey-2;
          color: $mid-grey-1;
          margin-left: 5px;
        }

        .tol-list ul li .remove {
          width: 30px;
          height: 30px;

          font-size: 1.2em;
          color: $row-border;
          text-align: center;
          float: right;
          &:hover {
            background-color: $row-border;
            border-radius: 100%;
            color: $white;
          }
        }
        .permissions {
          display: inline-flex;
        }

        .rate-form {
          margin-top: 10px;
          display: flex;
          flex-direction: column;

          //
          .rate-form-row {
            display: flex;
            padding-bottom: 12px;
            justify-content: space-between;
            input,
            textarea,
            select {
              width: auto;
              display: inline-block;
              border: 1px solid $mid-blue;
            }
          }
          .rate-form-btn {
            width: auto;
            margin-top: 12px;
            padding: 8px;
            color: $white;
            font-weight: bold;
            text-align: center;
            background-color: $green;
            &:hover {
              cursor: pointer;
            }
          }
        }

        .user-list-head,
        .user-list-body {
          display: flex;
          .left {
            flex-basis: 30%;
          }
          .right {
            flex-basis: 65%;
            .header {
              display: flex;
              .title {
                flex-basis: 50%;
                text-transform: uppercase;
                display: inline-flex;
                span {
                  flex-basis: 33%;
                  font-size: 8pt;
                }
              }
            }
            .content {
              display: inline-flex;
              width: 50%;
              span {
                flex-basis: 33%;
              }
            }
          }

          .action {
            display: inline-flex;
            position: relative;
            width: 30px;
            height: 30px;
            color: $row-border;
            svg {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
            }
            &:hover {
              background-color: $row-border;
              border-radius: 100%;
              color: $white;
            }
          }
        }

        .rate-list-head,
        .rate-list-body {
          display: flex;
          align-items: center;

          span {
            display: inline-block;
            flex-basis: 20%;
            position: relative;
            text-transform: uppercase;
            text-align: center;
            &.list-action {
              flex-basis: 30px;
              height: 30px;
              text-align: center;
              color: $row-border;
              margin-left: auto;
              svg {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
              }
              &:hover {
                background-color: $row-border;
                border-radius: 100%;
                color: $white;
              }
            }
          }
        }
        .rate-list-head {
          padding: 8px 0;
          background-color: $background-level-3;
        }
        .single-line {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          label {
            margin-right: 15px;
          }
          .payrollee-code {
            display: flex;
            flex-basis: 20px;
            justify-content: center;
            min-height: 25px;
            font-weight: bold;
            background-color: $blue;
            color: $white;
          }
          .list-action {
            text-align: right;
            &:hover {
              cursor: pointer;
            }
          }
        }
        .payrollee-code {
          margin-bottom: 12px;
          background-color: $blue;
        }
        .list-action {
          text-align: right;
          &:hover {
            cursor: pointer;
          }
        }
        .po-delete {
          color: $red;
          cursor: pointer;
          margin-right: 10px;
        }
        .po-display {
          display: flex;
          flex-grow: 2;
          align-items: center;
          justify-content: center;
        }
        .break {
          width: 100%;
          height: 0;
        }
      }

      .list-header {
        display: flex;
        justify-content: flex-end;
        border-bottom: 4px solid $row-border;
        font-weight: bold;
        span {
          display: inline-block;
          font-size: 1.2em;
          font-weight: bold;
          width: 20px;
          height: 20px;
          line-height: 20px;
          border-radius: 100%;
          text-align: center;
          &:hover {
            cursor: pointer;
            background-color: $light-grey-2;
          }
          &.history {
            display: none;
          }
        }
      }
    }

    .bordered {
      border: 1px solid $blue;
      font-size: 1em;
      &:focus {
        outline: none;
      }

      &.small {
        width: auto;
        display: inline-flex;
        margin-right: 15px;
      }

      &.form-number-input {
        width: 100px;
      }
    }

    .form-input {
      border-bottom: 1px solid $blue;
      font-size: 1em;
      height: inherit;
      &:focus {
        outline: none;
      }
      &.error {
        border-bottom: 1px solid $red;
      }

      &.small {
        width: auto;
        display: inline-flex;
        margin-right: 15px;
      }

      &.form-number-input {
        width: 50px;
        border: 1px solid $mid-blue;
        display: inline-flex;
        padding: 5px 0;
        &.wide {
          width: 100px;
        }
      }
    }

    .assignment-action-container {
      display: flex;
      position: relative;
      justify-content: space-between;
      background-color: $background-level-3;
      padding: 60px 20px;
      width: 100%;
      margin: 30px 0;

      .comment {
        display: flex;
        background-color: $white;
        flex-basis: 50%;
        div {
          flex-grow: 1;
        }
      }

      .assignment-action {
        width: 50px;
        height: 50px;
        font-size: 1.5em;
        margin-right: 20px;
        color: $white;
        border-radius: 100%;
        background-color: $secondary-green;
        &.submit {
          background-color: $orange;
        }
        &.submit:hover {
          background-color: $secondary-orange;
        }
        &.approve {
          background-color: $blue;
        }
        &.approve:hover {
          background-color: $dark-blue;
        }
        &.reject {
          background-color: $red;
        }
        &.reject:hover {
          background-color: $secondary-red;
        }
        &.disabled {
          background-color: $light-grey-2;
          color: $light-grey-1;
          &:hover {
            background-color: $light-grey-2;
            color: $light-grey-1;
            cursor: not-allowed;
          }
        }
      }
    }
  }

  &.modified {
    border-left: 5px solid $orange;
  }

  .error {
    color: $red;
    background-color: $modified-form;
  }
}

.horizontal-nav-tab {
  border-bottom: 1px solid $row-border;

  list-style: none;
  padding: 0;
  > li {
    text-transform: uppercase;
    display: inline-block;
    margin-right: 20px;
    cursor: pointer;
    &.active {
      border-bottom: 5px solid $row-border;
      color: $orange;
    }
  }
}
</style>
